import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { analyticsService, cleanProductName } from '../services/analyticsService';
import type { ConversationClassification, AnalyticsFilters } from '../types/analytics.types';

export interface ChartPoint {
  name: string;
  count: number;
}

export interface AnalyticsKpis {
  totalConversations: number;
  uniqueUsers: number;
  uniqueProducts: number;
  topSpecies: string;
}

export interface FilterOptions {
  species: string[];
  productiveTypesBySpecies: Record<string, string[]>;
  countries: string[];
  productLines: string[];
  productNamesByLine: Record<string, string[]>;
  months: string[];
}

const DEFAULT_FILTERS: AnalyticsFilters = {
  species: null,
  productiveType: null,
  country: null,
  productLine: null,
  productName: null,
  month: null,
};

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function sortDesc(points: ChartPoint[]): ChartPoint[] {
  return [...points].sort((a, b) => b.count - a.count);
}

export function useAnalytics() {
  const { getAccessToken } = useAuth();

  const [raw, setRaw] = useState<ConversationClassification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AnalyticsFilters>(DEFAULT_FILTERS);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAccessToken()
      .then((token) => analyticsService.getClassifications(token ?? ''))
      .then((data) => {
        if (!cancelled) {
          setRaw(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load analytics');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [getAccessToken]);

  // Hierarchical filterOptions — always from raw so dropdowns never shrink
  const filterOptions = useMemo((): FilterOptions => {
    const speciesSet = new Set<string>();
    const productiveTypesBySpecies: Record<string, Set<string>> = {};
    const countriesSet = new Set<string>();
    const productLinesSet = new Set<string>();
    const productNamesByLine: Record<string, Set<string>> = {};

    for (const r of raw) {
      if (r.species_group) {
        const sp = capitalize(r.species_group);
        speciesSet.add(sp);
        if (r.productive_type) {
          if (!productiveTypesBySpecies[sp]) productiveTypesBySpecies[sp] = new Set();
          productiveTypesBySpecies[sp].add(r.productive_type);
        }
      }
      if (r.user_country) countriesSet.add(r.user_country);
      if (r.product_line) {
        const line = cleanProductName(r.product_line) ?? r.product_line;
        productLinesSet.add(line);
        if (r.product_name) {
          const name = cleanProductName(r.product_name) ?? r.product_name;
          if (!productNamesByLine[line]) productNamesByLine[line] = new Set();
          productNamesByLine[line].add(name);
        }
      }
    }

    const monthsSet = new Set<string>();
    for (const r of raw) {
      const ym = r.conversation_date?.slice(0, 7);
      if (ym) monthsSet.add(ym);
    }

    return {
      species: [...speciesSet].sort(),
      productiveTypesBySpecies: Object.fromEntries(
        Object.entries(productiveTypesBySpecies).map(([k, v]) => [k, [...v].sort()]),
      ),
      countries: [...countriesSet].sort(),
      productLines: [...productLinesSet].sort(),
      productNamesByLine: Object.fromEntries(
        Object.entries(productNamesByLine).map(([k, v]) => [k, [...v].sort()]),
      ),
      months: [...monthsSet].sort(),
    };
  }, [raw]);

  const filtered = useMemo(() => {
    return raw.filter((r) => {
      if (filters.species && capitalize(r.species_group ?? '') !== filters.species) return false;
      if (filters.productiveType && r.productive_type !== filters.productiveType) return false;
      if (filters.country && r.user_country !== filters.country) return false;
      if (filters.productLine && cleanProductName(r.product_line) !== filters.productLine) return false;
      if (filters.productName && cleanProductName(r.product_name) !== filters.productName) return false;
      if (filters.month && (r.conversation_date?.slice(0, 7) ?? '') !== filters.month) return false;
      return true;
    });
  }, [raw, filters]);

  const kpis = useMemo((): AnalyticsKpis => {
    const uniqueUsers = new Set(filtered.map((r) => r.user_email).filter(Boolean)).size;
    // Count unique product lines (not sub-products)
    const uniqueProducts = new Set(
      filtered.map((r) => cleanProductName(r.product_line)).filter(Boolean),
    ).size;
    const speciesCounts: Record<string, number> = {};
    for (const r of filtered) {
      if (r.species_group) {
        const key = capitalize(r.species_group);
        speciesCounts[key] = (speciesCounts[key] ?? 0) + 1;
      }
    }
    const topSpecies = Object.entries(speciesCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
    return { totalConversations: filtered.length, uniqueUsers, uniqueProducts, topSpecies };
  }, [filtered]);

  // Dual-mode: top-level species_group OR drill-down productive_type when species is active
  const speciesData = useMemo((): ChartPoint[] => {
    const counts: Record<string, number> = {};
    if (filters.species) {
      for (const r of filtered) {
        const key = r.productive_type ?? '(unspecified)';
        counts[key] = (counts[key] ?? 0) + 1;
      }
    } else {
      for (const r of filtered) {
        if (r.species_group) {
          const key = capitalize(r.species_group);
          counts[key] = (counts[key] ?? 0) + 1;
        }
      }
    }
    return sortDesc(Object.entries(counts).map(([name, count]) => ({ name, count })));
  }, [filtered, filters.species]);

  // Dual-mode: product_line top-level OR drill-down product_name when productLine is active
  const productData = useMemo((): ChartPoint[] => {
    const counts: Record<string, number> = {};
    if (filters.productLine) {
      for (const r of filtered) {
        const name = cleanProductName(r.product_name);
        if (name) counts[name] = (counts[name] ?? 0) + 1;
      }
    } else {
      for (const r of filtered) {
        const line = cleanProductName(r.product_line);
        if (line) counts[line] = (counts[line] ?? 0) + 1;
      }
    }
    return sortDesc(Object.entries(counts).map(([name, count]) => ({ name, count }))).slice(0, 10);
  }, [filtered, filters.productLine]);

  const queryData = useMemo((): ChartPoint[] => {
    const counts: Record<string, number> = {};
    for (const r of filtered) {
      for (const param of r.query_parameters) {
        const key = param.trim().toLowerCase();
        if (key) counts[key] = (counts[key] ?? 0) + 1;
      }
    }
    return sortDesc(
      Object.entries(counts).map(([name, count]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        count,
      })),
    ).slice(0, 15);
  }, [filtered]);

  const countryData = useMemo((): ChartPoint[] => {
    const counts: Record<string, number> = {};
    for (const r of filtered) {
      if (r.user_country) counts[r.user_country] = (counts[r.user_country] ?? 0) + 1;
    }
    return sortDesc(Object.entries(counts).map(([name, count]) => ({ name, count })));
  }, [filtered]);

  const topUsersData = useMemo((): ChartPoint[] => {
    const counts: Record<string, number> = {};
    const labels: Record<string, string> = {};
    for (const r of filtered) {
      const key = r.user_email ?? r.user_name ?? 'Unknown';
      counts[key] = (counts[key] ?? 0) + 1;
      if (r.user_name && !labels[key]) labels[key] = r.user_name;
    }
    return sortDesc(
      Object.entries(counts).map(([email, count]) => ({
        name: labels[email] ?? email,
        count,
      })),
    ).slice(0, 10);
  }, [filtered]);

  return {
    loading,
    error,
    filters,
    setFilters,
    filterOptions,
    kpis,
    speciesData,
    productData,
    queryData,
    countryData,
    topUsersData,
  };
}
